import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, PenLine } from "lucide-react";
import { BRAND } from "@/config/brand";
import { useSeo } from "@/hooks/useSeo";
import { getPublishedArticles, formatArticleDate } from "@/content/articles";

export default function Blog() {
  useSeo({
    title: "Journal",
    description: `Notes and stories from the ${BRAND.name} team on private conversation, connection and AI companionship.`,
    path: "/blog",
  });

  const articles = getPublishedArticles();

  return (
    <div className="min-h-screen bg-[#0A0908] bg-noise px-5 py-8" data-testid="blog-index">
      <div className="mx-auto max-w-2xl">
        <Link to="/" className="mb-6 inline-flex items-center gap-1 text-sm text-[#8C8578] hover:text-[#D4AF37]" data-testid="blog-back-link">
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>

        <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">{BRAND.name}</p>
        <h1 className="mt-2 font-display text-4xl font-medium tracking-tight text-[#F4F1EA]">Journal</h1>
        <p className="mt-3 text-base text-[#8C8578]">
          Notes on private conversation, connection and fictional AI companionship.
        </p>

        {articles.length === 0 ? (
          <div className="mt-10 rounded-2xl border border-[#2B241B] bg-[#12100E] p-8 text-center" data-testid="blog-empty">
            <PenLine className="mx-auto h-6 w-6 text-[#D4AF37]" />
            <p className="mt-3 font-display text-xl text-[#F4F1EA]">Our first article is on its way</p>
            <p className="mt-2 text-sm text-[#8C8578]">We're still writing. Check back soon.</p>
          </div>
        ) : (
          <div className="mt-10 space-y-4">
            {articles.map((a) => (
              <Link
                key={a.slug}
                to={`/blog/${a.slug}`}
                data-testid={`blog-article-card-${a.slug}`}
                className="group block rounded-2xl border border-[#2B241B] bg-[#12100E] p-6 transition-colors hover:border-[#D4AF37]"
              >
                <div className="flex items-center gap-3 text-xs text-[#8C8578]">
                  <time dateTime={a.date}>{formatArticleDate(a.date)}</time>
                  <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {a.readMinutes} min read</span>
                </div>
                <h2 className="mt-2 font-display text-2xl font-medium tracking-tight text-[#F4F1EA] group-hover:text-[#D4AF37]">
                  {a.title}
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-[#C8C2B5]">{a.description}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm text-[#D4AF37]">
                  Read <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
